import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './ProfileDatePicker.scss';

const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Parse YYYY-MM-DD as local calendar date */
function parseLocalYmd(value: string): { y: number; m: number; d: number } | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [ys, ms, ds] = value.split('-');
  const y = Number(ys);
  const m = Number(ms) - 1;
  const d = Number(ds);
  if (m < 0 || m > 11 || d01(d, m, y)) return null;
  return { y, m, d };
}

function d01(d: number, m: number, y: number): boolean {
  const last = new Date(y, m + 1, 0).getDate();
  return d < 1 || d > last;
}

function toYmd(y: number, m: number, d: number): string {
  return `${y}-${pad2(m + 1)}-${pad2(d)}`;
}

function buildMonthCells(viewY: number, viewM: number): (number | null)[] {
  const firstDow = new Date(viewY, viewM, 1).getDay();
  const daysInMonth = new Date(viewY, viewM + 1, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDow; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export interface ProfileDatePickerProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  hasError?: boolean;
  /** If true, days after today cannot be selected (e.g. date of birth) */
  disableFuture?: boolean;
  /** If true, days before today cannot be selected (e.g. tournament / schedule dates) */
  disablePast?: boolean;
  /** When true the popover renders to document.body, escaping any `overflow:hidden`/scroll parents. */
  portal?: boolean;
}

type PanelMode = 'calendar' | 'years';

export function ProfileDatePicker({
  id,
  value,
  onChange,
  placeholder = 'Select date',
  disabled = false,
  className = '',
  hasError = false,
  disableFuture = false,
  disablePast = false,
  portal = false,
}: ProfileDatePickerProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const yearScrollRef = useRef<HTMLDivElement>(null);
  const selectedYearRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [panelMode, setPanelMode] = useState<PanelMode>('calendar');
  const [portalPos, setPortalPos] = useState<{ top: number; left: number } | null>(null);

  const parsed = useMemo(() => parseLocalYmd(value), [value]);
  const today = useMemo(() => {
    const t = new Date();
    return { y: t.getFullYear(), m: t.getMonth(), d: t.getDate() };
  }, []);

  const minYear = disablePast ? today.y : 1920;
  const maxYear = disableFuture ? today.y : today.y + 10;

  const yearRange = useMemo(() => {
    const list: number[] = [];
    const lo = Math.min(minYear, maxYear);
    const hi = Math.max(minYear, maxYear);
    for (let y = hi; y >= lo; y -= 1) list.push(y);
    return list;
  }, [minYear, maxYear]);

  const initialView = parsed ?? today;
  const [viewY, setViewY] = useState(initialView.y);
  const [viewM, setViewM] = useState(initialView.m);

  useEffect(() => {
    if (!open) return;
    if (parsed) {
      setViewY(parsed.y);
      setViewM(parsed.m);
    } else if (disablePast) {
      setViewY(today.y);
      setViewM(today.m);
    }
  }, [open, parsed, disablePast, today.y, today.m]);

  useEffect(() => {
    if (!open) setPanelMode('calendar');
  }, [open]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !portal) {
      setPortalPos(null);
      return;
    }
    const update = () => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setPortalPos({ top: r.bottom + 4, left: r.left });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, portal]);

  const cells = useMemo(() => buildMonthCells(viewY, viewM), [viewY, viewM]);

  const displayText = useMemo(() => {
    if (!value) return '';
    const p = parseLocalYmd(value);
    if (!p) return value;
    return `${MONTH_LABELS[p.m]} ${p.d}, ${p.y}`;
  }, [value]);

  const goPrevMonth = useCallback(() => {
    setViewM((m) => {
      if (m === 0) {
        setViewY((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
  }, []);

  const goNextMonth = useCallback(() => {
    setViewM((m) => {
      if (m === 11) {
        setViewY((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
  }, []);

  const isFutureDay = (day: number): boolean => {
    if (!disableFuture) return false;
    const chosen = new Date(viewY, viewM, day);
    const t = new Date();
    t.setHours(23, 59, 59, 999);
    return chosen.getTime() > t.getTime();
  };

  const isPastDay = (day: number): boolean => {
    if (!disablePast) return false;
    const chosen = new Date(viewY, viewM, day);
    chosen.setHours(0, 0, 0, 0);
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return chosen.getTime() < t.getTime();
  };

  const isDayDisabled = (day: number): boolean => isFutureDay(day) || isPastDay(day);

  const selectDay = (day: number) => {
    if (isDayDisabled(day)) return;
    onChange(toYmd(viewY, viewM, day));
    setOpen(false);
  };

  const isSelected = (day: number): boolean => {
    if (!parsed) return false;
    return parsed.y === viewY && parsed.m === viewM && parsed.d === day;
  };

  useEffect(() => {
    if (panelMode !== 'years' || !yearScrollRef.current) return;
    const t = window.setTimeout(() => {
      selectedYearRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 0);
    return () => window.clearTimeout(t);
  }, [panelMode, viewY]);

  const selectYear = (y: number) => {
    if (disableFuture && y > today.y) return;
    setViewY(y);
    setPanelMode('calendar');
  };

  const popover = open ? (
    <div
      ref={popoverRef}
      className={`profile-date-picker__popover${portal ? ' profile-date-picker__popover--portal' : ''}`}
      role="dialog"
      aria-label="Choose date"
      style={portal && portalPos ? { top: portalPos.top, left: portalPos.left } : undefined}
    >
      <div className="profile-date-picker__header">
        <div className="profile-date-picker__month-year">
          <button
            type="button"
            className="profile-date-picker__month-year-btn"
            onClick={() =>
              panelMode === 'calendar' ? setPanelMode('years') : setPanelMode('calendar')
            }
            aria-expanded={panelMode === 'years'}
          >
            <span>
              {MONTH_LABELS[viewM]} {viewY}
            </span>
            {panelMode === 'calendar' ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <polyline points="18 15 12 9 6 15" />
              </svg>
            )}
          </button>
        </div>
        {panelMode === 'calendar' && (
          <div className="profile-date-picker__nav">
            <button type="button" onClick={goPrevMonth} aria-label="Previous month">
              ‹
            </button>
            <button type="button" onClick={goNextMonth} aria-label="Next month">
              ›
            </button>
          </div>
        )}
      </div>

      {panelMode === 'calendar' && (
        <>
          <div className="profile-date-picker__weekdays">
            {WEEKDAYS.map((d, i) => (
              <span key={`${d}-${i}`}>{d}</span>
            ))}
          </div>

          <div className="profile-date-picker__grid">
            {cells.map((day, i) =>
              day == null ? (
                <span key={`e-${i}`} className="profile-date-picker__cell profile-date-picker__cell--empty" />
              ) : (
                <button
                  key={day}
                  type="button"
                  disabled={isDayDisabled(day)}
                  className={`profile-date-picker__cell${isSelected(day) ? ' is-selected' : ''}${
                    isDayDisabled(day) ? ' is-disabled' : ''
                  }`}
                  onClick={() => selectDay(day)}
                >
                  {day}
                </button>
              )
            )}
          </div>
        </>
      )}

      {panelMode === 'years' && (
        <div ref={yearScrollRef} className="profile-date-picker__year-scroll">
          <div className="profile-date-picker__year-grid">
            {yearRange.map((y) => (
              <button
                key={y}
                type="button"
                ref={y === viewY ? selectedYearRef : undefined}
                className={`profile-date-picker__year-cell${viewY === y ? ' is-selected' : ''}${
                  (disableFuture && y > today.y) || (disablePast && y < today.y) ? ' is-disabled' : ''
                }`}
                disabled={(disableFuture && y > today.y) || (disablePast && y < today.y)}
                onClick={() => selectYear(y)}
              >
                {y}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  ) : null;

  return (
    <div ref={wrapRef} className={`profile-date-picker ${className} ${hasError ? 'profile-date-picker--error' : ''}`}>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        className="profile-date-picker__trigger"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((o) => !o)}
      >
        <span className={value ? 'profile-date-picker__trigger-text' : 'profile-date-picker__trigger-placeholder'}>
          {value ? displayText : placeholder}
        </span>
        <span className="profile-date-picker__trigger-chevron" aria-hidden>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </span>
      </button>

      {portal ? (open && portalPos ? createPortal(popover, document.body) : null) : popover}
    </div>
  );
}
